"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Chaque tâche du quotidien que l'on confie à Claude Cowork, avec le livrable rendu.
const TASKS = [
    {
        id: "client",
        icon: "letter-bold-duotone",
        format: "email",
        chip: "Cw_del_client_chip",
        brief: "Cw_del_client_brief",
        title: "Cw_del_client_title",
        lines: ["Cw_del_client_l1", "Cw_del_client_l2"],
        insight: null,
    },
    {
        id: "meeting",
        icon: "notebook-bold-duotone",
        format: "list",
        chip: "Cw_del_meeting_chip",
        brief: "Cw_del_meeting_brief",
        title: "Cw_del_meeting_title",
        lines: ["Cw_del_meeting_l1", "Cw_del_meeting_l2", "Cw_del_meeting_l3"],
        insight: "Cw_del_meeting_insight",
    },
    {
        id: "sales",
        icon: "chart-2-bold-duotone",
        format: "list",
        chip: "Cw_del_sales_chip",
        brief: "Cw_del_sales_brief",
        title: "Cw_del_sales_title",
        lines: ["Cw_del_sales_l1", "Cw_del_sales_l2", "Cw_del_sales_l3"],
        insight: "Cw_del_sales_insight",
    },
    {
        id: "post",
        icon: "hashtag-square-bold-duotone",
        format: "post",
        chip: "Cw_del_post_chip",
        brief: "Cw_del_post_brief",
        title: "Cw_del_post_title",
        lines: ["Cw_del_post_l1"],
        insight: "Cw_del_post_tags",
    },
];

const listContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};
const lineItem = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

export default function CwDelegate() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [taskId, setTaskId] = useState("client");
    const [phase, setPhase] = useState("done"); // thinking | done

    const task = TASKS.find((x) => x.id === taskId);

    function pick(id) {
        setTaskId(id);
        setPhase(reduce ? "done" : "thinking");
    }

    useEffect(() => {
        if (phase !== "thinking") return undefined;
        const timer = setTimeout(() => setPhase("done"), 950);
        return () => clearTimeout(timer);
    }, [phase, taskId]);

    return (
        <section id="cw-delegate" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Cw_del_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Cw_del_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (corail Claude) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:claude"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        Claude Cowork
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-orange-300">
                            {t("Cw_del_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Choix de la tâche */}
                    <div className="flex flex-wrap items-center gap-2 mb-5">
                        {TASKS.map((x) => (
                            <button
                                key={x.id}
                                type="button"
                                onClick={() => pick(x.id)}
                                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition active:scale-95 ${
                                    taskId === x.id
                                        ? "bg-orange-500 text-white"
                                        : "bg-white/[0.06] ring-1 ring-white/15 text-gray-200 hover:bg-white/[0.1]"
                                }`}
                            >
                                <SolarIcon name={x.icon} className="w-4 h-4" />
                                {t(x.chip)}
                            </button>
                        ))}
                    </div>

                    {/* Votre demande */}
                    <div className="flex items-start gap-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3 mb-3">
                        <span className="flex items-center justify-center w-8 h-8 shrink-0 rounded-lg bg-white/10">
                            <SolarIcon
                                name="user-rounded-bold-duotone"
                                className="w-4 h-4 text-gray-300"
                            />
                        </span>
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-0.5">
                                {t("Cw_del_you_label")}
                            </div>
                            <p className="text-[13.5px] text-gray-100 leading-snug mb-0">
                                {t(task.brief)}
                            </p>
                        </div>
                    </div>

                    {/* Flèche / passage à Claude Cowork */}
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-3 pl-1">
                        <Icon
                            icon="logos:claude"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        {t("Cw_del_claude_label")}
                        <span className="flex-1 h-px bg-white/10 ml-1" />
                    </div>

                    {/* Livrable de Claude Cowork (artefact clair) */}
                    <div className="min-h-[230px] flex items-start justify-center">
                        <AnimatePresence mode="wait">
                            {phase === "thinking" ? (
                                <motion.div
                                    key="thinking"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="flex items-center gap-2 text-[13px] text-orange-200 pt-10"
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {[0, 1, 2].map((i) => (
                                            <motion.span
                                                key={i}
                                                className="w-1.5 h-1.5 rounded-full bg-orange-300"
                                                animate={{ opacity: [0.3, 1, 0.3] }}
                                                transition={{
                                                    duration: 0.9,
                                                    repeat: Infinity,
                                                    delay: i * 0.15,
                                                }}
                                            />
                                        ))}
                                    </span>
                                    {t("Cw_del_thinking")}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key={taskId}
                                    initial={reduce ? false : { opacity: 0, y: 12, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                    className="w-full max-w-[560px] rounded-2xl bg-white ring-1 ring-slate-200 shadow-xl overflow-hidden"
                                >
                                    {/* Accent */}
                                    <div className="h-1.5 bg-gradient-to-r from-orange-500 to-rose-400" />

                                    {/* En-tête du livrable */}
                                    <div className="flex items-center gap-2.5 px-5 py-3 border-b border-slate-100">
                                        <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-orange-100">
                                            <SolarIcon
                                                name={task.icon}
                                                className="w-4 h-4 text-orange-600"
                                            />
                                        </span>
                                        <h4 className="text-[14px] font-bold text-slate-900 leading-tight">
                                            {t(task.title)}
                                        </h4>
                                        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-3 h-3"
                                            />
                                            {t("Cw_del_done_tag")}
                                        </span>
                                    </div>

                                    {/* Corps du livrable */}
                                    <motion.div
                                        variants={reduce ? undefined : listContainer}
                                        initial={reduce ? false : "hidden"}
                                        animate="visible"
                                        className="p-5"
                                    >
                                        {task.format === "email" && (
                                            <div className="flex flex-col gap-2.5">
                                                {task.lines.map((lk) => (
                                                    <motion.p
                                                        key={lk}
                                                        variants={reduce ? undefined : lineItem}
                                                        className="text-[13.5px] text-slate-700 leading-relaxed mb-0"
                                                    >
                                                        {t(lk)}
                                                    </motion.p>
                                                ))}
                                            </div>
                                        )}

                                        {task.format === "list" && (
                                            <>
                                                <div className="flex flex-col gap-2.5">
                                                    {task.lines.map((lk) => (
                                                        <motion.div
                                                            key={lk}
                                                            variants={reduce ? undefined : lineItem}
                                                            className="flex items-start gap-2.5"
                                                        >
                                                            <SolarIcon
                                                                name="check-circle-bold"
                                                                className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5"
                                                            />
                                                            <span className="text-[13.5px] text-slate-700 leading-snug">
                                                                {t(lk)}
                                                            </span>
                                                        </motion.div>
                                                    ))}
                                                </div>
                                                {task.insight && (
                                                    <motion.div
                                                        variants={reduce ? undefined : lineItem}
                                                        className="mt-3 flex items-start gap-2 rounded-xl bg-orange-50 px-3 py-2.5"
                                                    >
                                                        <SolarIcon
                                                            name="lightbulb-bolt-bold-duotone"
                                                            className="w-4 h-4 text-orange-500 shrink-0 mt-0.5"
                                                        />
                                                        <p className="text-[13px] text-orange-800 leading-snug mb-0">
                                                            {t(task.insight)}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </>
                                        )}

                                        {task.format === "post" && (
                                            <>
                                                <motion.p
                                                    variants={reduce ? undefined : lineItem}
                                                    className="text-[14px] text-slate-800 leading-relaxed mb-0"
                                                >
                                                    {t(task.lines[0])}
                                                </motion.p>
                                                <motion.p
                                                    variants={reduce ? undefined : lineItem}
                                                    className="text-[13px] font-medium text-orange-600 mt-1.5 mb-0"
                                                >
                                                    {t(task.insight)}
                                                </motion.p>
                                                <motion.div
                                                    variants={reduce ? undefined : lineItem}
                                                    className="flex items-center gap-6 mt-4 pt-3 border-t border-slate-100 text-slate-400"
                                                >
                                                    <SolarIcon name="heart-angle-bold" className="w-4 h-4" />
                                                    <SolarIcon name="chat-round-line-bold-duotone" className="w-4 h-4" />
                                                    <SolarIcon name="plain-2-bold" className="w-4 h-4" />
                                                </motion.div>
                                            </>
                                        )}
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Cw_del_caption")}
            </p>
        </section>
    );
}
